import type { PlainWorkflow, PlainWorkflowExecution, PlainWorkflowTrigger } from "@/queries/automation-queries";
import {
    WorkflowExecutionAction_Status_StepStatus_StepPhase,
    WorkflowExecutionPhase,
    WorkflowTrigger_PullRequestEvent,
    type WorkflowExecutionTrigger,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import { Timestamp, type PlainMessage } from "@bufbuild/protobuf";
import type { OrganizationMember } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { AgentCodeContext, PullRequest } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { AutomationTemplateId } from "@/utils/automation-templates";
import type { FC } from "react";
import type { CentralIconBaseProps } from "central-icons/CentralIconBase";
import IconPlayCircle from "central-icons/IconPlayCircle";
import IconCalendarClock4 from "central-icons/IconCalendarClock4";
import IconPullRequest from "central-icons/IconPullRequest";
import { formatCronToReadable } from "@/utils/cron";

export enum WorkflowDetailsTab {
    Definition = "definition",
    Executions = "executions",
}

export function workflowDetailsTabFromLocation(location: { hash: string }): WorkflowDetailsTab | undefined {
    switch (location.hash) {
        case `#${WorkflowDetailsTab.Definition}`:
            return WorkflowDetailsTab.Definition;
        case `#${WorkflowDetailsTab.Executions}`:
            return WorkflowDetailsTab.Executions;
        default:
            return undefined;
    }
}

/**
 * Default workflow action limits.
 * These are used when creating new workflows or when existing workflows have missing/invalid limits.
 */
export const DEFAULT_WORKFLOW_LIMITS = {
    maxParallel: 10,
    maxTotal: 100,
} as const;

export const workflowsUrl = () => "/automations/";
export const webhooksUrl = () => "/automations/webhooks";
export const webhookDetailsUrl = (webhookId: string) => `/automations/webhooks/${webhookId}`;
export const newWorkflowUrl = ({ templateId }: { templateId: AutomationTemplateId }) =>
    `/automations/new?template=${templateId}`;
export const workflowDetailsUrl = ({ id, tab }: { id: string; tab?: WorkflowDetailsTab }) =>
    `/automations/${id}${tab ? `#${tab}` : ""}`;
export const workflowDetailsEditUrl = ({ id }: { id: string }) => `/automations/${id}/edit`;
export const workflowExecutionsUrl = ({ id }: { id: string }) => `/automations/executions/${id}`;
export const workflowExecutionActionUrl = ({ actionId, executionId }: { actionId: string; executionId: string }) =>
    `/automations/executions/${executionId}/actions/${actionId}`;

export const workflowName = (workflow?: PlainWorkflow) => workflow?.metadata?.name || workflow?.id;

export const workflowExecutionPhaseLabel = (workflowExecution: PlainWorkflowExecution) => {
    switch (workflowExecution.status?.phase) {
        case WorkflowExecutionPhase.UNSPECIFIED:
            return "Unspecified";
        case WorkflowExecutionPhase.PENDING:
            return "Pending";
        case WorkflowExecutionPhase.RUNNING:
            return "Running";
        case WorkflowExecutionPhase.STOPPING:
            return "Stopping";
        case WorkflowExecutionPhase.STOPPED:
            return hasFailure(workflowExecution) ? "Failed" : "Stopped";
        case WorkflowExecutionPhase.DELETING:
            return "Deleting";
        case WorkflowExecutionPhase.DELETED:
            return "Deleted";
        case WorkflowExecutionPhase.COMPLETED:
            return hasFailedActions(workflowExecution) ? "Failed" : "Completed";
        default:
            return "Unknown";
    }
};

export const workflowExecutionActionStepPhaseLabel = (
    phase?: WorkflowExecutionAction_Status_StepStatus_StepPhase,
    failureMessage?: string,
) => {
    if (phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.DONE && failureMessage) {
        return "Failed";
    }

    switch (phase) {
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.UNSPECIFIED:
            return "Unspecified";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED:
            return "Failed";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.PENDING:
            return "Pending";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.RUNNING:
            return "Running";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.DONE:
            return "Done";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.CANCELLED:
            return "Cancelled";
        default:
            return "Unknown";
    }
};

export const workflowCreatedDate = (workflow?: PlainWorkflow): Date | undefined => {
    const ts = workflow?.metadata?.createdAt;
    if (!ts) {
        return undefined;
    }
    return new Timestamp(ts).toDate();
};

export const workflowCreatorName = (
    workflow?: PlainWorkflow,
    members?: PlainMessage<OrganizationMember>[],
): string | undefined => {
    const creatorId = workflow?.metadata?.creator?.id;
    if (!creatorId || !members) {
        return undefined;
    }
    const memberLookup = Object.fromEntries(members.map((m) => [m.userId, m]));
    return memberLookup[creatorId]?.fullName;
};

export const triggerName = (trigger?: PlainWorkflowTrigger | PlainMessage<WorkflowExecutionTrigger>): string | null => {
    if (!trigger) return null;

    switch (trigger.trigger.case) {
        case "manual":
            return "Manual";
        case "pullRequest":
            return "Pull Request";
        case "time":
            return "Scheduled";
        default:
            return "Unknown";
    }
};

export type ExecutionDisplayName =
    | { type: "default"; label: string; id: string }
    | { type: "pullRequest"; label: string; pr: PlainMessage<PullRequest> };

/**
 * Generates a display name for a workflow execution.
 * For PR-triggered executions, returns PR details for rich rendering.
 * For other triggers, returns a simple label with the execution ID.
 */
export const executionDisplayName = (execution: PlainWorkflowExecution): ExecutionDisplayName => {
    const trigger = execution.spec?.trigger;
    const triggerType = triggerName(trigger);

    if (trigger?.trigger.case === "pullRequest") {
        const pr = trigger.trigger.value;
        return {
            type: "pullRequest",
            label: `[Webhook] PR #${pr.id}: ${pr.title}`,
            pr,
        };
    }

    return {
        type: "default",
        label: `[${triggerType ?? "Unknown"}]`,
        id: execution.id,
    };
};

export const triggerIcon = (
    trigger?: PlainWorkflowTrigger | PlainMessage<WorkflowExecutionTrigger>,
): FC<CentralIconBaseProps> => {
    if (!trigger) return IconPlayCircle;

    switch (trigger.trigger.case) {
        case "manual":
            return IconPlayCircle;
        case "pullRequest":
            return IconPullRequest;
        case "time":
            return IconCalendarClock4;
        default:
            return IconPlayCircle;
    }
};

export function triggerLabel(trigger?: PlainWorkflowTrigger): string {
    if (!trigger?.trigger?.case) return "Unknown trigger";

    switch (trigger.trigger.case) {
        case "manual":
            return "Manual trigger";
        case "time": {
            const cronExpression = trigger.trigger.value.cronExpression;
            if (cronExpression) {
                return formatCronToReadable(cronExpression) ?? "Scheduled trigger";
            }
            return "Scheduled trigger";
        }
        case "pullRequest":
            return "Pull request trigger";
        default:
            return "Unknown trigger";
    }
}

export const pullRequestEventName = (event: WorkflowTrigger_PullRequestEvent): string => {
    switch (event) {
        case WorkflowTrigger_PullRequestEvent.APPROVED:
            return "Approved";
        case WorkflowTrigger_PullRequestEvent.CLOSED:
            return "Closed";
        case WorkflowTrigger_PullRequestEvent.MERGED:
            return "Merged";
        case WorkflowTrigger_PullRequestEvent.OPENED:
            return "Opened";
        case WorkflowTrigger_PullRequestEvent.UPDATED:
            return "Updated";
        case WorkflowTrigger_PullRequestEvent.READY_FOR_REVIEW:
            return "Ready for Review";
        case WorkflowTrigger_PullRequestEvent.UNSPECIFIED:
        default:
            return "Unspecified";
    }
};

export const getProjectIDFromContext = (context?: PlainMessage<AgentCodeContext>): string | null => {
    switch (context?.context.case) {
        case "projectId":
            return context.context.value;
        default:
            return null;
    }
};

/**
 * Check if a workflow execution phase allows cancellation.
 * Only PENDING and RUNNING phases can be cancelled.
 */
const isCancellableExecutionPhase = (phase: WorkflowExecutionPhase): boolean => {
    return phase === WorkflowExecutionPhase.PENDING || phase === WorkflowExecutionPhase.RUNNING;
};

export const canCancel = (execution: PlainWorkflowExecution): boolean => {
    const phase = execution.status?.phase ?? WorkflowExecutionPhase.UNSPECIFIED;
    return isCancellableExecutionPhase(phase);
};

export const isWorkflowRunning = (execution?: PlainWorkflowExecution): boolean => {
    return execution?.status?.phase === WorkflowExecutionPhase.RUNNING;
};

export const isWorkflowCompleted = (execution?: PlainWorkflowExecution): boolean => {
    return execution?.status?.phase === WorkflowExecutionPhase.COMPLETED;
};

export const hasFailure = (execution: PlainWorkflowExecution): boolean => {
    return !!execution.status?.failureMessage;
};

export const hasFailedActions = (execution: PlainWorkflowExecution): boolean => {
    return (execution.status?.failedActionCount ?? 0) > 0;
};

export const hasManualTrigger = (workflow?: PlainWorkflow): boolean => {
    if (!workflow?.spec?.triggers) {
        return false;
    }
    return workflow.spec.triggers.some((trigger) => trigger.trigger?.case === "manual");
};

export const getWorkflowTriggerDescription = (workflow?: PlainWorkflow): string => {
    if (!workflow?.spec?.triggers || workflow.spec.triggers.length === 0) {
        return "This automation will create executions when triggered.";
    }

    const triggers = workflow.spec.triggers;
    const triggerDescriptions: string[] = [];

    for (const trigger of triggers) {
        switch (trigger.trigger?.case) {
            case "manual":
                triggerDescriptions.push("manually");
                break;
            case "pullRequest":
                triggerDescriptions.push("by a pull request event");
                break;
            case "time":
                triggerDescriptions.push("based on the configured schedule");
                break;
        }
    }

    if (triggerDescriptions.length === 0) {
        return "This automation will create executions when triggered.";
    }

    if (triggerDescriptions.length === 1) {
        return `This automation will create executions when triggered ${triggerDescriptions[0]}.`;
    }

    // Multiple triggers: "when triggered manually, by a pull request event, or based on the configured schedule"
    const lastTrigger = triggerDescriptions.pop();
    return `This automation will create executions when triggered ${triggerDescriptions.join(", ")}, or ${lastTrigger}.`;
};

/**
 * Check if a workflow is marked as deleting.
 */
export const isWorkflowDeleting = (workflow?: PlainWorkflow): boolean => {
    return workflow?.spec?.deleting === true;
};

/**
 * Get the manual trigger from a workflow, if one exists.
 */
export const getManualTrigger = (workflow?: PlainWorkflow): PlainWorkflowTrigger | undefined => {
    if (!workflow?.spec?.triggers) {
        return undefined;
    }
    return workflow.spec.triggers.find((trigger) => trigger.trigger?.case === "manual");
};
